// parseFieldErrors (dashboard-v2) — DRIFT-942 / DRIFT-912.
//
// Converges the v2 FormDialog error-handling API toward v1
// (web/dashboard/src/lib/parseFieldErrors.ts). The backend serialises 400/422
// validation failures (and AppException(field_errors=[...])) as:
//
//   { error?, detail?, field_errors?: [{ field_id, message, code? }] }
//
// (see src/api/app/core/exceptions.py — FieldError / field_id is snake_case and
// matches the FormDialog field `name`.) This helper splits that payload into:
//   - fieldErrors: a Record<fieldName, message> the FormDialog forwards to each
//     primitive's `error` prop (per-field a11y branch: role="alert" +
//     aria-invalid + aria-describedby), and
//   - bannerMessage: the form-wide banner string (top-level detail, or orphan
//     field errors whose field_id is not a rendered field).
//
// v2 keeps the slimmer shape (flat Record) the registry-driven FormDialog wants,
// rather than v1's react-hook-form `setError` list, but mirrors v1's routing
// semantics: exact-match field_id → per-field; nested/array dot-path → walk up
// to a known parent and prefix the residual suffix; no parent match → banner.

/** Mirrors the backend FieldError wire shape (field_id is snake_case). */
export interface FieldErrorEntry {
  field_id: string;
  message: string;
  code?: string | null;
}

export interface ParsedFieldErrors {
  /** Per-field errors keyed by field name (FormDialog `errors` prop input). */
  fieldErrors: Record<string, string>;
  /** Top-level message for the form-wide banner (FormDialog `error` prop). */
  bannerMessage: string | undefined;
  /** Observed HTTP status, when extractable. */
  statusCode: number | null;
  /** True when the payload carried a non-empty field_errors[]. */
  hadFieldErrors: boolean;
}

export type KnownFieldNames = Set<string> | string[];

const VALIDATION_CODES = new Set([400, 422]);

function asString(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

/**
 * Strip Pydantic v2's auto-prefix from validator-raised ValueError /
 * AssertionError messages ("Value error, <msg>") so users see the bare message.
 * Mirrors v1 stripPydanticPrefix; idempotent.
 */
export function stripPydanticPrefix(message: string): string {
  return message
    .replace(
      /^(Value error|Assertion error|ValueError|AssertionError)[,:]?\s+/i,
      "",
    )
    .trim();
}

function extractStatus(err: unknown): number | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  const response = anyErr.response as Record<string, unknown> | undefined;
  if (response && typeof response.status === "number") return response.status;
  if (typeof anyErr.status === "number") return anyErr.status;
  return null;
}

function extractBody(err: unknown): unknown {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  const response = anyErr.response as Record<string, unknown> | undefined;
  if (response && response.data !== undefined) return response.data;
  if (anyErr.data !== undefined) return anyErr.data;
  return null;
}

/**
 * Resolve a (possibly nested/array-indexed) field_id against the form's known
 * field names. Returns the matched name + residual suffix on a parent-walk
 * match; null when no segment prefix is a known field. Mirrors v1
 * resolveFieldTarget so the two dashboards route identical payloads the same
 * way. Rejects empty / pure-dot ids and skips malformed empty path segments.
 */
export function resolveFieldTarget(
  field_id: string,
  known: Set<string>,
): { matched: string; suffix: string } | null {
  if (typeof field_id !== "string" || field_id.length === 0) return null;
  if (/^\.+$/.test(field_id)) return null;
  if (known.has(field_id)) return { matched: field_id, suffix: "" };
  if (!field_id.includes(".")) return null;
  const parts = field_id.split(".");
  for (let i = parts.length - 1; i > 0; i--) {
    const prefixParts = parts.slice(0, i);
    const suffixParts = parts.slice(i);
    if (prefixParts.some((p) => p === "")) continue;
    if (suffixParts.some((p) => p === "")) continue;
    const candidate = prefixParts.join(".");
    if (known.has(candidate)) {
      return { matched: candidate, suffix: suffixParts.join(".") };
    }
  }
  return null;
}

/**
 * Parse a 4xx error response (axios-style or fetch-throw) into per-field errors
 * + a banner message. `knownFieldNames` are the FormDialog field `name`s, used
 * to discriminate per-field entries from orphans that roll up to the banner.
 */
export function parseFieldErrors(
  err: unknown,
  knownFieldNames: KnownFieldNames,
): ParsedFieldErrors {
  const known =
    knownFieldNames instanceof Set ? knownFieldNames : new Set(knownFieldNames);
  const status = extractStatus(err);
  const body = extractBody(err);

  const result: ParsedFieldErrors = {
    fieldErrors: {},
    bannerMessage: undefined,
    statusCode: status,
    hadFieldErrors: false,
  };

  if (!body || typeof body !== "object") {
    const fallback =
      asString((err as { message?: unknown } | null)?.message) ?? undefined;
    result.bannerMessage = fallback;
    return result;
  }

  const b = body as Record<string, unknown>;
  const detail = asString(b.detail) ?? asString(b.message);
  const fieldErrorsRaw = Array.isArray(b.field_errors) ? b.field_errors : null;
  const orphans: string[] = [];

  if (fieldErrorsRaw && fieldErrorsRaw.length > 0) {
    for (const raw of fieldErrorsRaw) {
      if (!raw || typeof raw !== "object") continue;
      const r = raw as Record<string, unknown>;
      const field_id = asString(r.field_id);
      let message = asString(r.message);
      if (!field_id || !message) continue;
      result.hadFieldErrors = true;
      message = stripPydanticPrefix(message);
      const target = resolveFieldTarget(field_id, known);
      if (target) {
        const finalMessage = target.suffix
          ? `${target.suffix}: ${message}`
          : message;
        // First error per field wins (matches v1 setError single-message-per-field).
        if (!(target.matched in result.fieldErrors)) {
          result.fieldErrors[target.matched] = finalMessage;
        }
      } else {
        orphans.push(`${field_id}: ${message}`);
      }
    }
  }

  if (detail) {
    result.bannerMessage = stripPydanticPrefix(detail);
  } else if (orphans.length > 0) {
    result.bannerMessage = orphans.join("; ");
  } else if (
    !result.hadFieldErrors &&
    status !== null &&
    !VALIDATION_CODES.has(status)
  ) {
    result.bannerMessage = "Request failed.";
  }

  return result;
}
